import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MintLayout } from '@solana/spl-token';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

import Processing from './Processing.js';
import { awaitTransactionSignatureConfirmation, mintOneToken } from './candyMachine.js';

const TX_TIMEOUT = 45000;
const MINT_PRICE = 0.11;

export default function MintButton({
  candyMachineId,
  candyMachineState,
  config,
  connection,
  numRemaining,
  refreshFn = () => null,
  timeRemaining = Infinity,
  treasury,
  wallet,
}) {
  const [isMinting, setIsMinting] = useState(false);
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [txPrice, setTxPrice] = useState();

  // Update the price once we establish a connection
  useEffect(() => {
    const getPrice = async () => {
      const { candyMachine } = candyMachineState || {};
      if (!candyMachine?.program || !candyMachineId) {
        return null;
      }
      const state = await candyMachine.program.account.candyMachine.fetch(candyMachineId);
      const { price: priceData } = state?.data || {};
      setTxPrice((priceData?.words[0] || Infinity) / LAMPORTS_PER_SOL);
    };
    getPrice();
  }, [candyMachineId, candyMachineState]);

  // Render disabled button if no candyMachineState
  if (!candyMachineState || !wallet) {
    return (
      <button className="text-lg sm:text-xl rounded font-mono font-bold p-2 disabled cursor-default">
        Connect WALLET ↗
      </button>
    );
  }

  /*
  // Don't render anything if the sale isn't live yet
  if (timeRemaining > 1000) {
    return null;
  }
  */

  // Otherwise, let's mint some Penguins
  const { candyMachine, itemsAvailable, itemsRedeemed, itemsRemaining, goLiveDate } = candyMachineState || {};

  const onMint = async () => {
    try {
      let errorMsg = 'MINT FAILED; TRY AGAIN!';
      setIsMinting(true);
      if (wallet && candyMachine?.program) {
        const mintTxId = await mintOneToken(candyMachine, config, wallet.publicKey, treasury).catch((e2) => {
          console.warn('e2.msg:', e2.msg);
          errorMsg = e2.msg.includes('not live yet') ? 'MINT NOT LIVE YET 😡' : e2.msg;
          toast.error(errorMsg);
          return;
        });
        const status = await awaitTransactionSignatureConfirmation(
          mintTxId,
          TX_TIMEOUT,
          connection,
          'singleGossip',
          false
        );
        if (!status?.err) {
          toast('ABUNAI DRAGON ACQUIRED');
        } else {
          toast.error(errorMsg, { autoClose: 8000 });
        }
      } else {
        toast.error('UNKNOWN ERROR; TRY AGAIN!', { autoClose: 8000 });
      }
    } catch (error) {
      let message = error.message || 'MINT FAILED; TRY AGAIN!';
      if (!error.msg) {
        if (error?.message?.indexOf('0x138')) {
          toast.error('MINT NOT LIVE', { autoClose: 8000 });
        } else if (error?.message?.indexOf('0x137')) {
          toast.error('SOLD OUT!');
        } else if (error?.message?.indexOf('0x135')) {
          toast.error('YOU NEED MORE SOL TO MINT', { autoClose: 8000 });
        } else {
          toast.error(message, { autoClose: 8000 });
        }
      } else {
        if (error.code === 311) {
          toast.error('SOLD OUT!');
          setIsSoldOut(true);
        } else if (error.code === 312) {
          toast.error('MINT NOT LIVE', { autoClose: 8000 });
        } else {
          toast.error(message, { autoClose: 8000 });
        }
      }
    } finally {
      setIsMinting(false);
      refreshFn();
    }
  };

  if (isSoldOut || numRemaining < 1) {
    return <h3 className="text-lg sm:text-xl md:text-2xl mt-2">SOLD OUT!</h3>;
  }

  return (
    <div className="m-4">
      <ToastContainer position="top-center" />
      {isMinting && <Processing />}
      {!isMinting && (
        <div className="flex flex-col">
          <button
            className="border-4 border-white bg-gradient-to-b rounded-xl shadow-xl text-xl sm:text-2xl md:text-3xl bg-gradient-to-b from-yellow-500 to-pink-600 hover:from-pink-500 hover:to-yellow-500 active:shadow-sm"
            onClick={onMint}
          >
            <div className="flex flex-col w-full h-full justify-center items-center p-4 pb-2">
              <span className="font-mono">MINT DRAGON</span>
              <div className="font-mono text-sm sm:text-base md:text-lg">{parseFloat(MINT_PRICE).toFixed(2)} SOL</div>
            </div>
          </button>
          {/*
          <div className="flex flex-col justify-center items-center">
            <p className="mt-6 text-lg">Price does not include tx fees</p>
            <p className="text-sm">
              Please have at least
              <span className="text-base font-mono font-bold">
                {' '}
                {(MINT_PRICE + txPrice + 0.01).toFixed(2)} SOL{' '}
              </span>
              in your wallet
            </p>
          </div>
          */}
        </div>
      )}
    </div>
  );
}
